// src/store/personalNotesStore.ts
import { create } from "zustand"
import { db } from "@/lib/firebase"
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore"
import { useAuthStore } from "@/store/authStore"
import { toast } from "@/hooks/use-toast"

export interface PersonalNote {
  id: string
  content: string
  date?: string
  title?: string
  createdAt: Date | null
  updatedAt: Date | null
  createdBy: string
}

interface PersonalNotesState {
  notes: PersonalNote[]
  loading: boolean
  init: () => void
  addNote: (date: string | undefined, title: string | undefined, content: string) => Promise<void>
  updateNote: (id: string, title: string | undefined, content: string) => Promise<void>
  deleteNote: (id: string) => Promise<void>
  getNotesForDate: (date: string) => PersonalNote[]
  getAllNotes: () => PersonalNote[]
  cleanup: () => void
}

let unsubscribe: (() => void) | null = null

export const usePersonalNotesStore = create<PersonalNotesState>((set, get) => ({
  notes: [],
  loading: false,

  init: () => {
    const user = useAuthStore.getState().user
    if (!user) {
      console.warn("[PersonalNotesStore] Пользователь не авторизован")
      return
    }

    if (unsubscribe) unsubscribe()

    console.log(`[PersonalNotesStore] Инициализация для пользователя: ${user.id}`)

    const q = query(
      collection(db, "personalNotes"),
      where("createdBy", "==", user.id.toString())
    )

    unsubscribe = onSnapshot(
      q,
      (snap) => {
        let loadedNotes: PersonalNote[] = snap.docs.map((d) => ({
          id: d.id,
          content: d.data().content || "",
          date: d.data().date || undefined,
          title: d.data().title || undefined,
          createdAt: d.data().createdAt?.toDate() || null,
          updatedAt: d.data().updatedAt?.toDate() || null,
          createdBy: d.data().createdBy,
        }))

        // СОРТИРОВКА: новые заметки сверху (по createdAt)
        loadedNotes.sort((a, b) => {
          const timeA = a.createdAt ? a.createdAt.getTime() : 0
          const timeB = b.createdAt ? b.createdAt.getTime() : 0
          return timeB - timeA // убывание (новые сначала)
        })

        console.log(`[PersonalNotesStore] Загружено и отсортировано ${loadedNotes.length} заметок`)
        set({ notes: loadedNotes, loading: false })
      },
      (err) => {
        console.error("Personal notes snapshot error:", err.code, err.message)
        set({ loading: false })
      }
    )
  },

  addNote: async (date: string | undefined, title: string | undefined, content: string) => {
    const user = useAuthStore.getState().user
    if (!user) {
      toast({ title: "Пользователь не авторизован", variant: "destructive" })
      return
    }

    if (!content || typeof content !== "string" || !content.trim()) {
      toast({ title: "Заметка не может быть пустой", variant: "destructive" })
      return
    }

    try {
      await addDoc(collection(db, "personalNotes"), {
        content: content.trim(),
        date: date || null,
        title: title?.trim() || null,
        createdBy: user.id.toString(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })

      console.log("✅ Заметка успешно добавлена")
      toast({ title: "Заметка сохранена" })
    } catch (err: any) {
      console.error("❌ Ошибка добавления заметки:", err)
      toast({
        title: "Не удалось сохранить заметку",
        description: err.message || "Проверьте подключение",
        variant: "destructive"
      })
    }
  },

  updateNote: async (id: string, title: string | undefined, content: string) => {
    if (!content || !content.trim()) return

    try {
      await updateDoc(doc(db, "personalNotes", id), {
        content: content.trim(),
        title: title?.trim() || null,
        updatedAt: serverTimestamp(),
      })
      toast({ title: "Заметка обновлена" })
    } catch (err: any) {
      console.error("Update note error:", err)
      toast({ title: "Не удалось обновить заметку", variant: "destructive" })
    }
  },

  deleteNote: async (id: string) => {
    try {
      await deleteDoc(doc(db, "personalNotes", id))
      toast({ title: "Заметка удалена", variant: "destructive" })
    } catch (err: any) {
      console.error("Delete note error:", err)
      toast({ title: "Не удалось удалить заметку", variant: "destructive" })
    }
  },

  getNotesForDate: (date: string) => get().notes.filter((n) => n.date === date),
  getAllNotes: () => get().notes,

  cleanup: () => {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    set({ notes: [] })
  },
}))